import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { LoadingSpinner } from "./ui/loadingSpinner/loadingSpinner";
import { useExternalControlSearch } from "../hooks/useExternalControls";
import { type ExternalControl } from "../models/audit/externalControl";

const PAGE_SIZE = 10;

interface ExternalControlPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  systemDisplayName: string;
  onControlSelected: (control: ExternalControl) => void;
}

export const ExternalControlPickerDialog = ({
  open,
  onOpenChange,
  systemDisplayName,
  onControlSelected,
}: ExternalControlPickerDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const {
    controls: paginatedControls,
    total: totalControls,
    loading,
  } = useExternalControlSearch(searchQuery, currentPage, open);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setCurrentPage(0);
    }
  }, [open]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery]);

  const totalPages = Math.ceil(totalControls / PAGE_SIZE);
  const startIndex = currentPage * PAGE_SIZE + 1;
  const endIndex = Math.min((currentPage + 1) * PAGE_SIZE, totalControls);

  const handleSelect = (control: ExternalControl) => {
    onControlSelected(control);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Pick a Control</DialogTitle>
          <DialogDescription>
            Search and select a control from {systemDisplayName}
          </DialogDescription>
        </DialogHeader>

        {/* Search bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, ID, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto border rounded-lg min-h-[200px]">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : paginatedControls.length === 0 ? (
            <div className="flex justify-center items-center py-12 text-sm text-gray-500">
              No controls found matching your search.
            </div>
          ) : (
            <div className="divide-y">
              {/* Already-linked controls stay visible (so a searched-for
                  control doesn't silently vanish) but can't be selected:
                  each Optro control can back at most one Midship control,
                  so creating another from it would fail at the auto-link
                  step after a full generation run. */}
              {paginatedControls.map((control) => (
                <button
                  key={control.externalRef}
                  className={`w-full p-4 text-left transition-colors ${
                    control.linked
                      ? "cursor-not-allowed opacity-60"
                      : "hover:bg-gray-50"
                  }`}
                  disabled={control.linked}
                  onClick={() => handleSelect(control)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{control.name}</span>
                    {control.linked && (
                      <Badge variant="secondary" className="text-xs">
                        Linked
                      </Badge>
                    )}
                    {control.idCode && (
                      <Badge variant="outline" className="text-xs">
                        {control.idCode}
                      </Badge>
                    )}
                  </div>
                  {control.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {control.description}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalControls > 0 && (
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-gray-500">
              Showing {startIndex}-{endIndex} of {totalControls}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
